// UploadControl.tsx
import React, { useState, useId, useRef } from 'react';
import { Control } from '../ControlRenderer';
import { Button, Typography } from '@mui/material';
import { FormModeType, useFormData } from '@/components/FormDataProvider';
import ModalBox, { Action } from '../ModalBox';
import { useConfig } from '@/components/ConfigProvider';
import { get, set } from '../utils/Utils';
import { useAuth } from '@/components/AuthProvider';
import useActionHandler from '@/hooks/useActionHandler';
import { useValidation } from '@/components/ValidationProvider';

export interface UploadControl extends Control {
  /**  * The action to be executed when the upload button is clicked.
   */
  uploadAction: Action;
  /**
   * Disable the upload button if the data context has 
   * validation errors. (Optional)
   */
  disableIfInvalid?: boolean;
}

interface UploadControlProps {
  control: Control;
  data: any;
  mode: FormModeType;
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

const UploadControl: React.FC<UploadControlProps> = ({ control, data, mode, onComplete, onError, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const inputId = useId(); // unique per component instance
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { apiUrl } = useConfig(); // Get the API root URL from config
  const { token } = useAuth(); // Get the bearer token
  const { formKey, formData, refreshForm } = useFormData();
  const { executeAction, UIAction, handleSuccess, handleFailure, handleClose, applyActionResult } = useActionHandler({ onComplete: onComplete, onError: onError, onClose: onClose });
  const { errors } = useValidation();
  //console.log(JSON.stringify(data))

  const uploadControl = control as UploadControl;
  const action = uploadControl.uploadAction ?? {};
  const isInvalid = uploadControl.disableIfInvalid && Object.keys(errors).length > 0;
  const accept = action.payload && action.payload.accept ? action.payload.accept : ".pdf,.doc,.docx,.jpg,.jpeg,.png";
  const uploadData = control.dataKey ? get(data, control.dataKey) : data;

  const getUploadErrorMessage = async (response: Response) => {
    const errorText = await response.text();

    if (response.status === 413 || /request entity is too large/i.test(errorText)) {
      return 'The selected file is too large to upload. The current upload limit is 250 MB.';
    }

    return errorText || 'Failed to upload file';
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    setSelectedFile(file || null);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {

    if (!selectedFile) {
      console.error('No file selected');
      return;
    }

    setUploading(true);

    try {
      if (action.payload && action.payload.endpoint) {
        const postData = new FormData();
        postData.append('file', selectedFile);

        if (action.payload.postDataKey) {
          const extraData = get(formData, action.payload.postDataKey);

          if (extraData && typeof extraData === 'object') {
            Object.entries(extraData).forEach(([key, value]) => {
              // FormData only supports strings/Blobs; stringify others if needed
              const v =
                value instanceof Blob || value instanceof File
                  ? value
                  : value != null
                    ? String(value)
                    : '';
              postData.append(key, v);
            });
          }
        } else {
          // original behavior
          postData.append('formKey', get(data, 'FormKey', formKey.toString()));
        }

        const response = await fetch(`${apiUrl}/${action.payload.endpoint}/upload`, {
          method: 'POST',
          body: postData,
          headers: {
            'Authorization': `Bearer ${token}`
            // Add any additional headers if needed
          }
        });

        if (!response.ok) {
          const message = await getUploadErrorMessage(response);
          set(formData, "lastApiError", message);
          throw new Error(message);
        }

        // if the target is an array add a new element otherwise copy the returned props
        var ret = await response.json();
        if (Array.isArray(uploadData)) {
          uploadData.unshift(ret);
          refreshForm();
        } else {
          applyActionResult(action, data, ret);
        }

        // File uploaded successfully
        console.log('File uploaded successfully');
        clearSelectedFile();
        handleSuccess(action, data);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      handleFailure(action, data);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
      <input
        ref={fileInputRef}
        type="file"
        id={inputId}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={accept}
      />
      <label htmlFor={inputId}>
        <Button variant="contained" component="span">
          Select File
        </Button>
      </label>
      <Typography variant="body1" gutterBottom style={{ margin: '0px 10px' }}>
        {selectedFile ? `Selected File: ${selectedFile.name}` : 'No file selected'}
      </Typography>
      {selectedFile && (
        <Button variant="contained" onClick={handleUpload} disabled={uploading || isInvalid}>
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction, data)}
          onError={() => handleFailure(UIAction, data)}
          onClose={() => handleClose(UIAction, data)} />}
    </div>
  );
};

export default UploadControl;
